import {
  PathsV1AppRoleSearchPostParametersQueryExtend,
  PathsV1AppRoleSearchPostParametersQueryOrderByDirection,
  PathsV1AppRoleSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { useDebounce } from "@uidotdev/usehooks";
import { EditIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";

import { useAppRoleSearchQuery } from "@/common/api/app-role/use-app-role-search.query";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableHeightMode, ImplementedColumnDef } from "@/components/data-table/types";
import { UserAppRoleRow, userAppRoleTableDefaultColumns } from "./user-app-role-table-columns";

const DEFAULT_EXTEND = [
  PathsV1AppRoleSearchPostParametersQueryExtend.Application,
  PathsV1AppRoleSearchPostParametersQueryExtend.Groups,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeBranch,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeBranch_Address,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeCompany,
  PathsV1AppRoleSearchPostParametersQueryExtend.AttributeCompanyGroup,
] as const;

const DEFAULT_COLUMNS: ImplementedColumnDef<UserAppRoleRow, unknown>[] = [
  userAppRoleTableDefaultColumns.columnAppRoleName as ImplementedColumnDef<UserAppRoleRow, unknown>,
  userAppRoleTableDefaultColumns.columnApplication as ImplementedColumnDef<UserAppRoleRow, unknown>,
  userAppRoleTableDefaultColumns.columnGroups as ImplementedColumnDef<UserAppRoleRow, unknown>,
];

const DEFAULT_ORDER_BY = [
  {
    direction: PathsV1AppRoleSearchPostParametersQueryOrderByDirection.Asc,
    field: PathsV1AppRoleSearchPostParametersQueryOrderByField.Name,
  },
];

export interface UserAppRoleTableProps {
  userUuid: string;
  heightMode?: DataTableHeightMode.Viewport | DataTableHeightMode.Content;
}

export function UserAppRoleTable({ userUuid, heightMode = DataTableHeightMode.Content }: UserAppRoleTableProps) {
  const { t } = useTranslation(["ram", "common"]);
  const [, navigate] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationState, setPaginationState] = useState({ page: 1, pageSize: 5 });
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data, isPending, isError } = useAppRoleSearchQuery({
    options: {
      parameters: {
        page: paginationState.page,
        pageSize: paginationState.pageSize,
        search: debouncedSearch || undefined,
        orderBy: orderBy,
        extend: [...DEFAULT_EXTEND],
      },
      body: { users: [userUuid] },
    },
    enabled: Boolean(userUuid),
  });

  const rows = data?.data ?? [];

  const tableActions = useMemo(
    () => ({
      onSearchChange: (value: string) => {
        setSearchTerm(value);
        setPaginationState((prev) => ({ ...prev, page: 1 }));
      },
      onPaginationChange: setPaginationState,
      onSortingChange: (sorting: Array<{ field: string; direction: "asc" | "desc" }>) => {
        setOrderBy(
          sorting.map((s) => ({
            field: s.field as PathsV1AppRoleSearchPostParametersQueryOrderByField,
            direction: s.direction as PathsV1AppRoleSearchPostParametersQueryOrderByDirection,
          })),
        );
        setPaginationState((prev) => ({ ...prev, page: 1 }));
      },
    }),
    [],
  );

  const rowActions = useMemo(
    () => (row: { original: UserAppRoleRow }) => {
      const applicationUuid = row.original.application?.uuid;
      if (!applicationUuid) return [];
      return [
        {
          label: t("common:Edit"),
          icon: <EditIcon className="h-4 w-4" />,
          onClick: () => navigate(`~/ram/application/${applicationUuid}/app-role/${row.original.uuid}/update`),
        },
      ];
    },
    [navigate, t],
  );

  return (
    <DataTable
      columns={DEFAULT_COLUMNS}
      data={rows}
      isLoading={isPending}
      isError={isError}
      heightMode={heightMode}
      searchValue={searchTerm}
      searchPlaceholder={t("ram:SearchByAppOrAppRole")}
      initialSorting={[{ id: "name", desc: false }]}
      paginationState={paginationState}
      totalItems={data?.meta?.total}
      serverSidePagination={true}
      actions={tableActions}
      rowActions={rowActions}
      features={{
        search: true,
        pagination: true,
        multiSelection: false,
        expansion: false,
      }}
    />
  );
}
